import {Typography} from 'components/Typography';
import {PRODUCT_REVIEWS_SORT_LIST} from 'modules/products/constants';
import {SortingSelect} from 'plugins/modules/sorting/components/SortingSelect';
import {ProductReviewCard} from '../ProductReviewCard';
import style from './style.module.scss';
import React, {useContext, useEffect, useMemo, useRef} from 'react';
import classNames from 'classnames';
import Icon from 'components/Icon';
import {ESortingActions} from 'modules/products/providers/CatalogPageProvider/types';
import {Loader} from 'components/Loader';
import {useReviewsScroll} from 'modules/products/hooks/useReviewsScroll';
import {useReviewsSearchParams} from 'modules/products/hooks/useReviewsSearchParams';
import {pluralReviewsText} from 'modules/products/utils';
import {useSortingControl} from 'hooks/product/useSortingControl';
import {ProductReviewContext} from 'modules/products/context/ProductReview';
import {useReviewsTotal} from 'modules/products/hooks/useReviewsTotal';
import {useRouter} from 'next/router';
import {scrolltoHashWithOffset} from 'core/helpers/screen';
import {usePageAnchor} from 'core/hooks/usePageAnchor';
import {useReviewScrollOffset} from 'modules/products/hooks/useReviewScrollOffset';

export const ProductReviews = () => {
    const {rating} = useReviewsSearchParams();
    const reviewsBoundRef = useRef<HTMLDivElement>(null);
    const {isLoading, onSortClick, createOnFilterClickHandler, data, getReviews} = useContext(ProductReviewContext);
    const {query} = useRouter();
    const total = useReviewsTotal({code: query.code as string});

    const reviews = useMemo(() => data?.items || [], [data]);

    const anchor = usePageAnchor();
    const reviewScrollOffset = useReviewScrollOffset();

    useEffect(() => {
        if (anchor) {
            scrolltoHashWithOffset(anchor, reviewScrollOffset);
        }
    }, [anchor, reviews, reviewScrollOffset]);

    const {sortingList} = useSortingControl({
        defaultValue: ESortingActions.POPULAR,
        options: PRODUCT_REVIEWS_SORT_LIST,
        sortingValues: ESortingActions,
    });

    const ratingFilterButtons = useMemo(
        () =>
            total && (
                <div className={style.ratingFilterWrapper}>
                    {[...total.assessments]
                        .sort((a, b) => Number(b.title) - Number(a.title))
                        .filter((item) => item.count)
                        .map((item) => {
                            return (
                                <div
                                    className={classNames(style.ratingFilterItem, {
                                        [style.filterActive]: rating === item.title.toString(),
                                    })}
                                    key={item.title.toString()}
                                    onClick={createOnFilterClickHandler(item.title)}
                                >
                                    <Icon icon={'star'} />
                                    <Typography variant={'label-18-m'}>{item.title}</Typography>
                                </div>
                            );
                        })}
                </div>
            ),
        [createOnFilterClickHandler, rating, total],
    );

    useReviewsScroll({reviewsBoundRef, data, isLoading, getReviews});

    return (
        <div id={'rating-list'}>
            <div className={style.mobileSorting}>
                <Typography className={style.count} color={'gray60'} variant={'p-medium'}>
                    {pluralReviewsText({count: reviews.length})}
                </Typography>
                <SortingSelect<ESortingActions>
                    className={style.sorting}
                    onSortChange={onSortClick}
                    sortingList={sortingList}
                />
            </div>
            <div className={style.ratingFilters}>
                <div className={style.tags}>
                    <Typography className={style.filtersText} color={'gray60'}>
                        Фильтр:
                    </Typography>
                    {ratingFilterButtons}
                </div>
                <div className={style.selectSorting}>
                    <SortingSelect<ESortingActions>
                        className={style.sorting}
                        onSortChange={onSortClick}
                        sortingList={sortingList}
                    />
                </div>
            </div>
            <div className={style.listWrapper}>
                {reviews.map((review) => (
                    <div className={style.reviewCardContainer} id={`${review.id}`} key={review.id}>
                        <ProductReviewCard
                            collapsed={false}
                            className={style.reviewCard}
                            review={review}
                            withScroll={false}
                        />
                    </div>
                ))}
                <div ref={reviewsBoundRef} />
            </div>
            <div className={style.loaderWrapper}>{isLoading && <Loader size={'xl'} />}</div>
        </div>
    );
};
